import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiBookOpen, FiPlay, FiClock, FiAward, FiCheckCircle, FiTrendingUp,
  FiCalendar, FiFilter, FiSearch, FiStar, FiUsers, FiDownload,
  FiBookmark, FiPlayCircle, FiLock, FiUnlock, FiBarChart2, FiBook,
  FiFileText, FiCode, FiPackage, FiShoppingCart, FiHeart, FiShare2,
  FiEye, FiTarget, FiZap, FiLayers, FiGrid, FiList
} = FiIcons;

const LMSPortal = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('courses');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCourse, setSelectedCourse] = useState(null);

  const courses = [
    {
      id: 1,
      title: 'Advanced Implantology Masterclass',
      instructor: 'Dr. Sarah Mitchell',
      description: 'Comprehensive course covering advanced implant placement techniques, bone grafting, and complex case management.',
      duration: '12 hours',
      credits: 12,
      level: 'Advanced',
      rating: 4.9,
      reviews: 234,
      students: 342,
      price: 499,
      discountedPrice: 349,
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=200&fit=crop',
      progress: 65,
      enrolled: true,
      category: 'Clinical',
      modules: [
        { title: 'Patient Selection & Planning', completed: true, duration: '2h', lessons: 8 },
        { title: 'Bone Grafting Techniques', completed: true, duration: '3h', lessons: 12 },
        { title: 'Implant Placement Protocols', completed: true, duration: '4h', lessons: 15 },
        { title: 'Complex Case Management', completed: false, duration: '3h', lessons: 10 }
      ],
      tags: ['implants', 'oral-surgery', 'advanced'],
      includes: ['Video Lectures', 'PDF Resources', 'Quizzes', 'Certificate'],
      lastAccessed: '2 days ago'
    },
    {
      id: 2,
      title: 'Digital Workflow in Modern Dentistry',
      instructor: 'Dr. Michael Chen',
      description: 'Master digital scanning, CAD/CAM, and 3D printing technologies to streamline your practice workflow.',
      duration: '8 hours',
      credits: 8,
      level: 'Intermediate',
      rating: 4.8,
      reviews: 187,
      students: 287,
      price: 299,
      discountedPrice: 209,
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=200&fit=crop',
      progress: 30,
      enrolled: true,
      category: 'Technology',
      modules: [
        { title: 'Introduction to Digital Dentistry', completed: true, duration: '1h', lessons: 5 },
        { title: 'Intraoral Scanning', completed: true, duration: '2h', lessons: 8 },
        { title: 'CAD/CAM Fundamentals', completed: false, duration: '3h', lessons: 12 },
        { title: '3D Printing Applications', completed: false, duration: '2h', lessons: 7 }
      ],
      tags: ['digital-dentistry', 'technology', 'cad-cam'],
      includes: ['Video Lectures', 'Software Demos', 'Templates', 'Certificate'],
      lastAccessed: '1 day ago'
    },
    {
      id: 3,
      title: 'Orthodontic Treatment Planning',
      instructor: 'Dr. Lisa Rodriguez',
      description: 'Learn comprehensive treatment planning for various orthodontic cases using traditional and clear aligner techniques.',
      duration: '10 hours',
      credits: 10,
      level: 'Intermediate',
      rating: 4.7,
      reviews: 142,
      students: 198,
      price: 399,
      image: 'https://images.unsplash.com/photo-1600950335869-4865b86238c9?w=400&h=200&fit=crop',
      progress: 0,
      enrolled: false,
      category: 'Orthodontics',
      tags: ['orthodontics', 'treatment-planning', 'aligners'],
      includes: ['Video Lectures', 'Case Studies', 'Templates', 'Certificate']
    }
  ];

  const bundles = [
    {
      id: 1,
      name: 'Complete Implantology Bundle',
      description: 'Everything you need to master implant dentistry',
      courses: 3,
      ebooks: 5,
      sops: 10,
      price: 1497,
      discountedPrice: 748,
      savings: 749,
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=200&fit=crop',
      items: [
        'Advanced Implantology Masterclass',
        'Bone Grafting Techniques',
        'Complex Cases Workshop',
        '5 Implant eBooks',
        '10 SOP Templates',
        'Lifetime Updates'
      ],
      enrolled: false
    },
    {
      id: 2,
      name: 'Digital Practice Transformation',
      description: 'Complete digital workflow for modern practices',
      courses: 4,
      ebooks: 8,
      automation: 15,
      price: 1999,
      discountedPrice: 999,
      savings: 1000,
      image: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=400&h=200&fit=crop',
      items: [
        '4 Digital Dentistry Courses',
        '8 Technology eBooks',
        '15 Automation Scripts',
        'Practice SOPs',
        'Support & Updates'
      ],
      enrolled: true
    }
  ];

  const ebooks = [
    {
      id: 1,
      title: 'The Complete Guide to Dental Implants',
      author: 'Dr. Sarah Mitchell',
      pages: 245,
      format: 'PDF',
      price: 49,
      discountedPrice: 39,
      rating: 4.8,
      downloads: 1234,
      image: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=200&h=300&fit=crop',
      category: 'Clinical',
      description: 'Comprehensive guide covering all aspects of implant dentistry from diagnosis to restoration.'
    },
    {
      id: 2,
      title: 'Digital Dentistry Handbook',
      author: 'Dr. Michael Chen',
      pages: 180,
      format: 'PDF + ePub',
      price: 39,
      discountedPrice: 29,
      rating: 4.9,
      downloads: 987,
      image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=200&h=300&fit=crop',
      category: 'Technology',
      description: 'Essential guide to implementing digital workflows in your practice.'
    }
  ];

  const automation = [
    {
      id: 1,
      name: 'Patient Follow-up Automation',
      description: 'Automated email sequences for post-treatment follow-ups',
      category: 'Communication',
      price: 79,
      downloads: 456,
      rating: 4.7,
      includes: ['Email Templates', 'SMS Scripts', 'Setup Guide']
    },
    {
      id: 2,
      name: 'Appointment Reminder System',
      description: 'Complete automated reminder workflow with customizable templates',
      category: 'Scheduling',
      price: 99,
      downloads: 678,
      rating: 4.9,
      includes: ['Email Templates', 'SMS Templates', 'Voice Scripts', 'Integration Guide']
    }
  ];

  const sops = [
    {
      id: 1,
      name: 'Complete Practice Management SOPs',
      description: '50+ standard operating procedures for dental practices',
      templates: 50,
      price: 199,
      downloads: 234,
      rating: 4.8,
      categories: ['Front Office', 'Clinical', 'Sterilization', 'Emergency']
    },
    {
      id: 2,
      name: 'Implant Surgery Protocols',
      description: 'Step-by-step protocols for implant procedures',
      templates: 15,
      price: 149,
      downloads: 189,
      rating: 4.9,
      categories: ['Pre-op', 'Surgery', 'Post-op', 'Complications']
    }
  ];

  const certifications = [
    {
      id: 1,
      title: 'Certified Digital Dentistry Specialist',
      issuer: 'DentalBridge Academy',
      description: 'Demonstrate expertise in digital dental technologies and workflows.',
      requirements: ['Complete Digital Workflow Course', 'Pass Assessment Exam', 'Submit Case Studies'],
      duration: '6 months',
      status: 'in-progress',
      progress: 45,
      badge: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=100&h=100&fit=crop'
    },
    {
      id: 2,
      title: 'Advanced Implantology Certification',
      issuer: 'International Implant Association',
      description: 'Advanced certification for experienced implant practitioners.',
      requirements: ['Complete Masterclass', 'Clinical Experience', 'Board Examination'],
      duration: '12 months',
      status: 'available',
      progress: 0,
      badge: 'https://images.unsplash.com/photo-1589395937658-0e03d46d502e?w=100&h=100&fit=crop'
    }
  ];

  const webinars = [
    {
      id: 1,
      title: 'Emerging Trends in Cosmetic Dentistry',
      speaker: 'Dr. James Wilson',
      date: '2024-01-20',
      time: '2:00 PM EST',
      duration: '90 minutes',
      registered: true,
      attendees: 234,
      category: 'cosmetic',
      thumbnail: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=200&fit=crop'
    },
    {
      id: 2,
      title: 'Practice Growth Strategies for 2024',
      speaker: 'Dr. Sarah Mitchell',
      date: '2024-01-25',
      time: '1:00 PM EST',
      duration: '60 minutes',
      registered: false,
      attendees: 567,
      category: 'business',
      thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=200&fit=crop'
    }
  ];

  const getLevelColor = (level) => {
    const colors = {
      Beginner: 'bg-green-100 text-green-800',
      Intermediate: 'bg-yellow-100 text-yellow-800',
      Advanced: 'bg-red-100 text-red-800'
    };
    return colors[level] || colors.Beginner;
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Learning Management System</h1>
              <p className="text-gray-600 mt-1">Courses, Bundles, eBooks, Automation Scripts, and SOPs</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm text-gray-600">CE Credits Earned</p>
                <p className="text-2xl font-bold text-gray-900">24/50</p>
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiAward} className="w-4 h-4" />
                <span>View Certificates</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">3</p>
              <p className="text-xs text-gray-600">Courses</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiPackage} className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">2</p>
              <p className="text-xs text-gray-600">Bundles</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiBook} className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">12</p>
              <p className="text-xs text-gray-600">eBooks</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiCode} className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">8</p>
              <p className="text-xs text-gray-600">Scripts</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiFileText} className="w-6 h-6 text-indigo-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">50</p>
              <p className="text-xs text-gray-600">SOPs</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <SafeIcon icon={FiAward} className="w-6 h-6 text-pink-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">2</p>
              <p className="text-xs text-gray-600">Certs</p>
            </div>
          </div>
        </motion.div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex space-x-6 overflow-x-auto">
              {[
                { id: 'courses', label: 'My Courses', icon: FiBookOpen, count: courses.filter(c => c.enrolled).length },
                { id: 'catalog', label: 'Course Catalog', icon: FiGrid, count: courses.length },
                { id: 'bundles', label: 'Bundles', icon: FiPackage, count: bundles.length },
                { id: 'ebooks', label: 'eBooks', icon: FiBook, count: ebooks.length },
                { id: 'automation', label: 'Automation', icon: FiCode, count: automation.length },
                { id: 'sops', label: 'SOPs', icon: FiFileText, count: sops.length },
                { id: 'certifications', label: 'Certifications', icon: FiAward, count: certifications.length },
                { id: 'webinars', label: 'Webinars', icon: FiCalendar, count: webinars.length }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                    }
                  `}
                >
                  <SafeIcon icon={tab.icon} className="w-4 h-4" />
                  <span>{tab.label}</span>
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
                />
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiFilter} className="w-4 h-4" />
              </button>
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                >
                  <SafeIcon icon={FiGrid} className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                >
                  <SafeIcon icon={FiList} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {/* My Courses */}
          {activeTab === 'courses' && (
            <motion.div
              key="courses"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {filteredCourses.filter(course => course.enrolled).map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <img src={course.image} alt={course.title} className="w-full h-48 object-cover" />
                    <div className="absolute top-4 right-4 flex items-center space-x-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getLevelColor(course.level)}`}>
                        {course.level}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-600">Progress</span>
                          <span className="font-medium text-gray-900">{course.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-cyan-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${course.progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded">
                        {course.category}
                      </span>
                      <span className="text-xs text-gray-500">Last: {course.lastAccessed}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{course.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{course.description}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiUsers} className="w-4 h-4" />
                        <span>{course.instructor}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>{course.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiAward} className="w-4 h-4" />
                        <span>{course.credits} CE</span>
                      </div>
                    </div>

                    <button className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
                      <SafeIcon icon={FiPlayCircle} className="w-5 h-5" />
                      <span>Continue Learning</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Course Catalog */}
          {activeTab === 'catalog' && (
            <motion.div
              key="catalog"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            >
              {filteredCourses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <img src={course.image} alt={course.title} className="w-full h-40 object-cover" />
                    {course.enrolled ? (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Enrolled
                        </span>
                      </div>
                    ) : (
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getLevelColor(course.level)}`}>
                          {course.level}
                        </span>
                      </div>
                    )}
                    {course.discountedPrice && (
                      <div className="absolute top-4 left-4">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-red-500 text-white">
                          SALE
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{course.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>
                    
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <SafeIcon
                            key={i}
                            icon={FiStar}
                            className={`w-4 h-4 ${i < Math.floor(course.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">{course.rating}</span>
                      <span className="text-sm text-gray-400">({course.reviews})</span>
                    </div>

                    <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-4 h-4" />
                        <span>{course.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiUsers} className="w-4 h-4" />
                        <span>{course.students}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiAward} className="w-4 h-4" />
                        <span>{course.credits} CE</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      {course.discountedPrice ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl font-bold text-gray-900">${course.discountedPrice}</span>
                          <span className="text-sm text-gray-400 line-through">${course.price}</span>
                        </div>
                      ) : (
                        <span className="text-2xl font-bold text-gray-900">${course.price}</span>
                      )}
                    </div>

                    <button className={`w-full py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                      course.enrolled 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}>
                      <SafeIcon icon={course.enrolled ? FiPlay : FiShoppingCart} className="w-4 h-4" />
                      <span>{course.enrolled ? 'Continue' : 'Enroll Now'}</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Bundles */}
          {activeTab === 'bundles' && (
            <motion.div
              key="bundles"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {bundles.map((bundle, index) => (
                <motion.div
                  key={bundle.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-sm border-2 border-purple-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <img src={bundle.image} alt={bundle.name} className="w-full h-48 object-cover" />
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1.5 text-sm font-bold rounded-lg bg-red-500 text-white shadow-lg">
                        SAVE ${bundle.savings}
                      </span>
                    </div>
                    {bundle.enrolled && (
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Enrolled
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <SafeIcon icon={FiPackage} className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-semibold text-purple-600">BUNDLE DEAL</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{bundle.name}</h3>
                    <p className="text-gray-600 mb-4">{bundle.description}</p>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {bundle.courses && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                          <p className="text-lg font-bold text-gray-900">{bundle.courses}</p>
                          <p className="text-xs text-gray-600">Courses</p>
                        </div>
                      )}
                      {bundle.ebooks && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <SafeIcon icon={FiBook} className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                          <p className="text-lg font-bold text-gray-900">{bundle.ebooks}</p>
                          <p className="text-xs text-gray-600">eBooks</p>
                        </div>
                      )}
                      {bundle.sops && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <SafeIcon icon={FiFileText} className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                          <p className="text-lg font-bold text-gray-900">{bundle.sops}</p>
                          <p className="text-xs text-gray-600">SOPs</p>
                        </div>
                      )}
                      {bundle.automation && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <SafeIcon icon={FiCode} className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="text-lg font-bold text-gray-900">{bundle.automation}</p>
                          <p className="text-xs text-gray-600">Scripts</p>
                        </div>
                      )}
                    </div>

                    <div className="mb-4 p-4 bg-white rounded-lg">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Bundle Includes:</p>
                      <ul className="space-y-1">
                        {bundle.items.map((item, i) => (
                          <li key={i} className="flex items-center space-x-2 text-sm text-gray-600">
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 line-through">${bundle.price}</p>
                        <p className="text-3xl font-bold text-green-600">${bundle.discountedPrice}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">You Save</p>
                        <p className="text-2xl font-bold text-green-600">${bundle.savings}</p>
                      </div>
                    </div>

                    <button className={`w-full py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 font-semibold ${
                      bundle.enrolled 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                    }`}>
                      <SafeIcon icon={bundle.enrolled ? FiUnlock : FiShoppingCart} className="w-5 h-5" />
                      <span>{bundle.enrolled ? 'Access Bundle' : 'Get Bundle Now'}</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* eBooks */}
          {activeTab === 'ebooks' && (
            <motion.div
              key="ebooks"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {ebooks.map((ebook, index) => (
                <motion.div
                  key={ebook.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <img src={ebook.image} alt={ebook.title} className="w-full h-64 object-cover" />
                    {ebook.discountedPrice && (
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-red-500 text-white">
                          SALE
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">{ebook.title}</h3>
                    <p className="text-xs text-gray-600 mb-2">{ebook.author}</p>
                    
                    <div className="flex items-center space-x-1 mb-3">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <SafeIcon
                            key={i}
                            icon={FiStar}
                            className={`w-3 h-3 ${i < Math.floor(ebook.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-600">{ebook.rating}</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span>{ebook.pages} pages</span>
                      <span>{ebook.format}</span>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      {ebook.discountedPrice ? (
                        <div className="flex items-center space-x-1">
                          <span className="text-lg font-bold text-gray-900">${ebook.discountedPrice}</span>
                          <span className="text-xs text-gray-400 line-through">${ebook.price}</span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">${ebook.price}</span>
                      )}
                    </div>

                    <button className="w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex items-center justify-center space-x-2">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Purchase</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Automation Scripts */}
          {activeTab === 'automation' && (
            <motion.div
              key="automation"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {automation.map((script, index) => (
                <motion.div
                  key={script.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <SafeIcon icon={FiCode} className="w-6 h-6 text-green-600" />
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      {script.category}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{script.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{script.description}</p>

                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Includes:</p>
                    <div className="flex flex-wrap gap-2">
                      {script.includes.map((item, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 text-sm">
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiDownload} className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{script.downloads} downloads</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-gray-900 font-semibold">{script.rating}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-gray-900">${script.price}</span>
                  </div>

                  <button className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    <span>Purchase Script</span>
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* SOPs */}
          {activeTab === 'sops' && (
            <motion.div
              key="sops"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {sops.map((sop, index) => (
                <motion.div
                  key={sop.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-indigo-50 rounded-lg">
                      <SafeIcon icon={FiFileText} className="w-6 h-6 text-indigo-600" />
                    </div>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full">
                      {sop.templates} Templates
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{sop.name}</h3>
                  <p className="text-gray-600 mb-4">{sop.description}</p>

                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Categories:</p>
                    <div className="flex flex-wrap gap-2">
                      {sop.categories.map((cat, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 text-sm">
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiDownload} className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{sop.downloads} downloads</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-gray-900 font-semibold">{sop.rating}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-3xl font-bold text-gray-900">${sop.price}</span>
                  </div>

                  <button className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiDownload} className="w-5 h-5" />
                    <span>Purchase SOPs</span>
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Certifications */}
          {activeTab === 'certifications' && (
            <motion.div
              key="certifications"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {certifications.map((cert, index) => (
                <motion.div
                  key={cert.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <img src={cert.badge} alt="" className="w-16 h-16 rounded-lg object-cover" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{cert.title}</h3>
                        <p className="text-sm text-gray-600">{cert.issuer}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      cert.status === 'in-progress' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {cert.status === 'in-progress' ? 'In Progress' : 'Available'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{cert.description}</p>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">Duration: {cert.duration}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">Requirements:</p>
                      <ul className="space-y-1">
                        {cert.requirements.map((req, i) => (
                          <li key={i} className="flex items-center space-x-2 text-sm text-gray-600">
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {cert.status === 'in-progress' && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-medium text-gray-900">{cert.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all"
                          style={{ width: `${cert.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <button className={`w-full py-2.5 rounded-lg transition-colors font-semibold ${
                    cert.status === 'in-progress'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                    {cert.status === 'in-progress' ? 'Continue Certification' : 'Start Certification'}
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Webinars */}
          {activeTab === 'webinars' && (
            <motion.div
              key="webinars"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {webinars.map((webinar, index) => (
                <motion.div
                  key={webinar.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <img src={webinar.thumbnail} alt={webinar.title} className="w-full h-48 object-cover" />
                    {webinar.registered ? (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Registered
                        </span>
                      </div>
                    ) : (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                          Available
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-sm">
                          <SafeIcon icon={FiUsers} className="w-4 h-4 text-gray-600" />
                          <span className="text-gray-900 font-medium">{webinar.attendees} registered</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded capitalize">
                        {webinar.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{webinar.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">by {webinar.speaker}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center space-x-3 text-sm">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{new Date(webinar.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm">
                        <SafeIcon icon={FiClock} className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{webinar.time} • {webinar.duration}</span>
                      </div>
                    </div>

                    <button className={`w-full py-2.5 rounded-lg transition-colors font-semibold ${
                      webinar.registered
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}>
                      {webinar.registered ? 'View Details' : 'Register Now'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LMSPortal;